/** PATCH helper for the line-set nested editor. The whole set (its fields +
 *  routes + stop-links + breaks) is rebuilt into one link-wrapped body and sent
 *  as a single PATCH. Sets carry `updated_ts`, so the write sends an
 *  `expected_updated_ts` token and a 409 (`route_set_modified`) sets the
 *  conflict flag for the ConflictBanner (the set token guards the whole nested
 *  edit). JSON only — line sets have no per-request file part. */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as routeSetsApi from '@/api/routeSets';
import type { RouteSetDetail, RouteSetWriteJson } from '@/api/routeSets';
import { useSnackbar } from '@/components/Snackbar';

export function useRouteSetPatch(set: RouteSetDetail, onSaved: () => void) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [conflict, setConflict] = useState(false);

  const mutation = useMutation({
    mutationFn: (body: RouteSetWriteJson) => {
      const payload = { ...body, expected_updated_ts: set.updated_ts };
      return routeSetsApi.update(set.id, payload);
    },
    onSuccess: () => {
      setConflict(false);
      snackbar.show('Saved.', 'success');
      qc.invalidateQueries({ queryKey: ['route_sets'] });
      onSaved();
    },
    onError: (e) => {
      if (e instanceof ConsoleApiError && e.status === 409) {
        setConflict(true);
        return;
      }
      const msg = e instanceof ConsoleApiError ? e.message : 'Save failed.';
      snackbar.show(msg, 'error');
    },
  });

  return {
    save: (body: RouteSetWriteJson) => mutation.mutate(body),
    saving: mutation.isPending,
    conflict,
    dismissConflict: () => setConflict(false),
    /** Field-keyed server errors from a 400, if any. */
    fieldErrors:
      mutation.error instanceof ConsoleApiError && mutation.error.status === 400
        ? (mutation.error.body as Record<string, unknown>)
        : null,
  };
}
